import { useContext, useState } from "react";
import useEntriesSummary from "@shared/features/bots/BotForm/OrdersSummary/useEntriesSummary";
import useStopSummary from "@shared/features/bots/BotForm/OrdersSummary/useStopSummary";
import useTakeProfitsSummary from "@shared/features/bots/BotForm/OrdersSummary/useTakeProfitsSummary";
import { BotFormType, Steps } from "@shared/features/bots/BotForm/common/types";
import { BotData, BotType, LanguageNameSpaces, UsageType } from "@shared/types";
import { BotFormContext } from "@shared/features/bots/BotForm/common/BotForm.context";
import { SignalsBotSummaryTab } from "@shared/features/bots/types";
import useBotFormDefaultValues from "@shared/features/bots/BotForm/useBotFormDefaultValues";
import { UseQuotaModal } from "@shared/features/settings/types";
import useTypedTranslation from "@shared/common/i18n/useTypedTranslation";
import { isSymbol } from "@shared/types/utils";
import { useExchangesMetadata } from "@shared/utils/hooks/misc";

interface UseSummaryProps {
  tabsWithErrors: Set<Steps>;
  isTwoLinesSummary?: boolean;
  useQuotaModal: UseQuotaModal;
}

const useSummary = ({
  tabsWithErrors,
  isTwoLinesSummary,
  useQuotaModal,
}: UseSummaryProps) => {
  const { t } = useTypedTranslation(LanguageNameSpaces.BOTS);
  const {
    botFormType,
    bulkActionValidBotsData,
    botType,
    tabToRestoreChangesData,
    isAdvancedView,
    setIsAdvancedView,
    tab,
    setTab,
    initializeFormValues,
    subGroupBotConfiguration,
    isChannelConfiguration,
    showBacktesting,
    account,
  } = useContext(BotFormContext)!;
  const restoreDefaultValues = useBotFormDefaultValues();
  const entriesSummaryData = useEntriesSummary({ isTwoLinesSummary });
  const takeProfitsSummaryData = useTakeProfitsSummary();
  const stopSummaryData = useStopSummary();
  const [summaryDrawerOpen, setSummaryDrawerOpen] = useState(false);
  const [chartDrawerOpen, setChartDrawerOpen] = useState(false);
  const [configurationModalBot, setConfigurationModalBot] = useState<BotData>();
  const [gridsSummaryModalOpen, setGridsSummaryModalOpen] = useState(false);
  const [backToBasicModalOpen, setBackToBasicModalOpen] = useState(false);
  const [openBacktestModal, setOpenBacktestModal] = useState(false);
  const { getIsFree } = useExchangesMetadata();
  const {
    quotaModal,
    handleTriggerClick,
    isLoading: isLoadingQuotaCheck,
  } = useQuotaModal({
    usageType: UsageType.BACKTESTING,
  });

  const isGridBot = botType === BotType.GRID;
  const isSignalsBot = botType === BotType.SIGNAL;
  const isBulkAction =
    botFormType === BotFormType.BULK_EDIT ||
    botFormType === BotFormType.BULK_CREATE;

  const onAdvancedSettingsClick = () => {
    if (isAdvancedView) setBackToBasicModalOpen(true);
    else {
      setIsAdvancedView(true);
      if (isGridBot && tab !== Steps.ENTRIES) {
        setTab(Steps.ENTRIES);
      }
    }
  };

  const getErrors = () => {
    if (isBulkAction && !bulkActionValidBotsData?.length) {
      return t("summary.errors.at_least_one_valid_bot");
    } else if (tabsWithErrors.size) {
      return isAdvancedView
        ? `${t("summary.errors.resolve_errors_tabs")} ${Array.from(
            tabsWithErrors
          )
            // The same names as the tab bar (useBotForm).
            .map((tab) =>
              tab === Steps.ENTRIES && isGridBot
                ? t("tabs.grids")
                : tab === Steps.ENTRIES && botType === BotType.HEDGING
                ? t("labels.hedging")
                : t(tab)
            )
            .join(", ")}.`
        : t("summary.errors.resolve_errors");
    }
  };

  const getBotTemplateConfigurationWarning = () => {
    if (botType !== BotType.SIGNAL) return;
    const hasPendingChanges = Object.values(tabToRestoreChangesData || {}).some(
      (resetData) => resetData?.valuesChanged
    );
    if (hasPendingChanges)
      return t("summary.warnings.group_configuration_modified");
  };

  const onResetToConfigurationClick = () => {
    initializeFormValues?.(subGroupBotConfiguration);
  };

  const errors = getErrors();

  const closeSummaryDrawer = () => {
    setChartDrawerOpen(false);
    setSummaryDrawerOpen(false);
  };

  const closeBackToBasicsModal = () => setBackToBasicModalOpen(false);
  const backToBasicsModalData = {
    isOpen: backToBasicModalOpen,
    title: t("summary.back_to_basic_modal.title"),
    text: t("summary.back_to_basic_modal.content"),
    onCancel: closeBackToBasicsModal,
    onOk: () => {
      restoreDefaultValues();
      setIsAdvancedView(false);
      closeBackToBasicsModal();
    },
  };

  const backtestingButtonData = {
    text: t("buttons.backtesting"),
    onClick: () => {
      if (!isSymbol(account) && account && getIsFree(account.exchange)) {
        setOpenBacktestModal(true);
      } else {
        handleTriggerClick().then((isQuotaError) => {
          if (!isQuotaError) {
            setOpenBacktestModal(true);
          }
        });
      }
    },
    isLoading: isLoadingQuotaCheck,
  };

  const backtestModalData = {
    open: openBacktestModal,
    closeModal: () => setOpenBacktestModal(false),
  };

  return {
    errors,
    summaryDrawerOpen,
    chartDrawerOpen,
    setSummaryDrawerOpen,
    setChartDrawerOpen,
    entriesSummaryData,
    takeProfitsSummaryData,
    stopSummaryData,
    configurationModalData: {
      isOpen: !!configurationModalBot,
      closeModal: () => setConfigurationModalBot(undefined),
      botData: configurationModalBot,
    },
    showBotConfiguration: setConfigurationModalBot,
    botTemplateConfigurationWarning: getBotTemplateConfigurationWarning(),
    gridsSummaryModalOpen,
    setGridsSummaryModalOpen,
    closeSummaryDrawer,
    advancedButtonText: isAdvancedView
      ? t("buttons.basic_settings")
      : t("buttons.advanced_settings"),
    onAdvancedSettingsClick,
    backToBasicsModalData,
    onResetToConfigurationClick,
    showAdvancedButton: isGridBot || (isSignalsBot && !isChannelConfiguration),
    backtestingButtonData,
    backtestModalData,
    signalsDefaultSummaryTab: openBacktestModal
      ? SignalsBotSummaryTab.BACKTESTING_HISTORY
      : undefined,
    quotaModal,
    showBacktesting,
  };
};

export default useSummary;
